import { track } from './analytics';
import {
  LANDING_EXPERIMENT_KEY,
  landingExperimentProps,
  type LandingVariant,
} from './landing-experiment';

declare global {
  interface Window {
    gtagSendEvent: (eventName: string) => void;
    fbq: (...args: unknown[]) => void;
    ttq: {
      track: (event: string, params?: Record<string, unknown>) => void;
      page: () => void;
      identify: (params: Record<string, unknown>) => void;
    };
    rdt?: (...args: unknown[]) => void;
  }
}

interface LandingTrackingOptions {
  variant: LandingVariant;
  hero?: string;
}

/**
 * Shared conversion tracking for both landing arms. Keeping one implementation
 * guarantees the control page and the motion arm fire the same Amplitude and
 * pixel events; only the ``landing_variant`` property differs.
 */
export function createLandingTracking({
  variant,
  hero,
}: LandingTrackingOptions) {
  const experimentProps = landingExperimentProps(variant);

  const getEventProperties = (props?: Record<string, unknown>) => ({
    ...experimentProps,
    ...(hero ? { hero } : {}),
    ...(props ?? {}),
  });

  return {
    variant,
    experiment: LANDING_EXPERIMENT_KEY,
    trackLandingScreen: () => {
      track('Landing Screen', getEventProperties());
      window.ttq?.track('ViewContent', {
        contents: [
          {
            content_id: 'landing_page',
            content_type: 'product',
            content_name: 'Harbor Landing Page',
          },
        ],
      });
    },
    handleAppStoreClick: (location: string) => {
      track('Click App Store', getEventProperties({ location }));
      if (typeof window.gtagSendEvent === 'function') {
        window.gtagSendEvent('click_app_store');
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'ClickAppStore', { location });
        window.fbq('track', 'Lead', { content_name: 'App Store', location });
      }
      if (window.ttq) {
        window.ttq.track('ClickButton', {
          contents: [
            {
              content_id: 'app_store',
              content_type: 'product',
              content_name: 'App Store',
            },
          ],
        });
        window.ttq.track('Download', {
          contents: [
            {
              content_id: 'app_store',
              content_type: 'product',
              content_name: 'Harbor iOS App',
            },
          ],
        });
      }
      if (typeof window.rdt === 'function') {
        window.rdt('track', 'Lead');
      }
    },
    handleGooglePlayClick: (location: string) => {
      track('Click Google Play', getEventProperties({ location }));
      if (typeof window.gtagSendEvent === 'function') {
        window.gtagSendEvent('click_google_play');
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'ClickGooglePlay', { location });
        window.fbq('track', 'Lead', { content_name: 'Google Play', location });
      }
      if (window.ttq) {
        window.ttq.track('ClickButton', {
          contents: [
            {
              content_id: 'google_play',
              content_type: 'product',
              content_name: 'Google Play',
            },
          ],
        });
        window.ttq.track('Download', {
          contents: [
            {
              content_id: 'google_play',
              content_type: 'product',
              content_name: 'Harbor Android App',
            },
          ],
        });
      }
      if (typeof window.rdt === 'function') {
        window.rdt('track', 'Lead');
      }
    },
    handleSignUpClick: () => {
      track('Click Sign Up', getEventProperties());
      if (typeof window.gtagSendEvent === 'function') {
        window.gtagSendEvent('click_sign_up');
      }
      if (window.ttq) {
        window.ttq.track('ClickButton', {
          contents: [
            {
              content_id: 'sign_up',
              content_type: 'product',
              content_name: 'Sign Up',
            },
          ],
        });
      }
      if (typeof window.rdt === 'function') {
        window.rdt('track', 'SignUp');
      }
    },
    handleLogInClick: () => {
      track('Click Log In', getEventProperties());
    },
    handleNavClick: (section: string) => {
      track('Click Nav Link', getEventProperties({ section }));
    },
  };
}

export type LandingTracking = ReturnType<typeof createLandingTracking>;
